import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Home, Building2, CalendarRange, ClipboardList } from "lucide-react";

const OFFICE_ROLES = ["owner", "manager"];

/**
 * Destinations adaptees au role :
 * - Chantiers  : owner/manager -> tous les chantiers ; foreman/worker -> les leurs.
 * - Planning   : worker -> son planning perso ; les autres -> le planning equipe.
 * - Taches     : owner/manager -> dashboard /tasks ; ouvrier/chef -> /my-tasks.
 * "Plus" est retire : le menu complet reste accessible via le hamburger du Topbar.
 */
function tabsFor(role) {
  const office = OFFICE_ROLES.includes(role);
  return [
    { key: "home", label: "Accueil", icon: Home, to: "/dashboard" },
    {
      key: "sites",
      label: "Chantiers",
      icon: Building2,
      to: office ? "/sites" : "/my-sites",
    },
    {
      key: "planning",
      label: "Planning",
      icon: CalendarRange,
      to: role === "worker" ? "/my-schedule" : "/planning",
    },
    {
      key: "tasks",
      label: "Tâches",
      icon: ClipboardList,
      to: office ? "/tasks" : "/my-tasks",
    },
  ];
}

/**
 * Barre de navigation mobile fixe en bas (etape B chantier mobile).
 * Visible uniquement sous le breakpoint md.
 */
export default function BottomNav({ user }) {
  const location = useLocation();
  const navigate = useNavigate();
  const role = user?.role || "owner";
  const tabs = tabsFor(role);

  const isActive = (to) =>
    location.pathname === to || location.pathname.startsWith(to + "/");

  // BUG-039 — cette barre et le bandeau cookies sont tous deux en
  // `fixed bottom-0`, le bandeau au-dessus (z-60 contre z-40). Mesure du
  // 20/09 a 320, 375 et 390 px : « Accueil » et « Chantiers » sortaient
  // RECOUVERTS sur les cinq ecrans testes. Un premier visiteur mobile ne
  // pouvait donc pas naviguer tant qu'il n'avait pas repondu au bandeau — et
  // la matrice de viewports du projet, qui ne mesure que le debordement
  // horizontal, restait verte.
  //
  // La barre annonce sa hauteur ; le bandeau s'en sert pour se poser au-dessus
  // d'elle. Une variable CSS plutot qu'une valeur en dur des deux cotes : la
  // barre n'existe pas sur les pages publiques, et le bandeau doit alors
  // retomber au ras du bas.
  useEffect(() => {
    const racine = document.documentElement;
    racine.style.setProperty("--cf-bottom-nav-h", "4rem");
    return () => racine.style.removeProperty("--cf-bottom-nav-h");
  }, []);

  return (
    <nav
      className="md:hidden fixed inset-x-0 bottom-0 z-40 bg-card border-t"
      style={{
        borderColor: "var(--cf-border)",
        paddingBottom: "env(safe-area-inset-bottom)",
      }}
      data-testid="mobile-bottom-nav"
    >
      <div className="grid grid-cols-4">
        {tabs.map((t) => {
          const active = isActive(t.to);
          const Icon = t.icon;
          return (
            <button
              key={t.key}
              type="button"
              onClick={() => navigate(t.to)}
              className="flex flex-col items-center justify-center gap-0.5 h-16"
              style={{ color: active ? "var(--cf-blue)" : "var(--cf-muted)" }}
              data-testid={`mobile-nav-${t.key}`}
            >
              <Icon className="h-[22px] w-[22px]" />
              <span className="text-[11px] font-medium">{t.label}</span>
            </button>
          );
        })}
      </div>
    </nav>
  );
}
